import { domQuerySelector } from 'pf-frontend-common/src/helper/dom/query-selector';
import { BrowserWindowEvent } from 'pf-frontend-common/src/module/browser/window.event';
import { DataKeyValueStringInterface } from 'pf-frontend-common/src/module/data/key-value/string.interface';
import { DomElementEvent } from 'pf-frontend-common/src/module/dom/element.event';
import { WindowServiceInterface } from 'pf-frontend-common/src/service/window/service.interface';

import { TooltipServiceInterface } from 'common/service/tooltip/service.interface';

import { TooltipViewOptionsInterface } from './view-options.interface';

export class TooltipView implements TooltipServiceInterface {
  /**
   * @inheritDoc
   */
  private element: HTMLElement;

  /**
   * Element which tooltip content should be rendered inside
   */
  private contentElement: HTMLElement;

  /**
   * Is the tooltip currently visible
   */
  private isVisible: boolean;

  /**
   * CSS Selectors
   */
  private readonly cssSelector: DataKeyValueStringInterface = {
    tooltipContent: '.phone-tooltip__content',
  };

  /**
   * CSS Classes
   */
  private readonly cssClass: DataKeyValueStringInterface = {
    tooltip: 'phone-tooltip',
    tooltipVisible: 'phone-tooltip--visible',
    tooltipContent: 'phone-tooltip__content',
  };

  /**
   * Constructor
   */
  constructor(private windowService: WindowServiceInterface) {}

  /**
   * @inheritDoc
   */
  public initialize(options: TooltipViewOptionsInterface): void {
    this.reset();

    this.element = this.createTooltipTemplate(options.element);
    this.contentElement = domQuerySelector(this.element, this.cssSelector.tooltipContent);
  }

  /**
   * @inheritDoc
   */
  public getContentElement(): HTMLElement {
    return this.contentElement;
  }

  /**
   * @inheritDoc
   */
  public open(): void {
    this.detachListeners();

    requestAnimationFrame(() => {
      this.setVisibility(true);
    });

    this.element.addEventListener(DomElementEvent.click, this.onClickElement);
    this.windowService.getNative().addEventListener(DomElementEvent.click, this.onClickWindow);
    this.windowService.getEventEmitter().addListener(BrowserWindowEvent.resize, this.onResizeWindow);
    this.windowService.getEventEmitter().addListener(BrowserWindowEvent.scroll, this.onScrollWindow);
  }

  /**
   * @inheritDoc
   */
  public reset(): void {
    if (this.element) {
      this.element.classList.remove(this.cssClass.tooltipVisible);
      this.element.parentNode.removeChild(this.element);
      this.element.removeEventListener(DomElementEvent.click, this.onClickElement);
    }

    this.isVisible = false;
    this.element = null;
    this.contentElement = null;

    this.detachListeners();
  }

  /**
   * Detach/remove window event listeners
   */
  private detachListeners(): void {
    this.windowService.getNative().removeEventListener(DomElementEvent.click, this.onClickWindow);
    this.windowService.getEventEmitter().removeListener(BrowserWindowEvent.resize, this.onResizeWindow);
    this.windowService.getEventEmitter().removeListener(BrowserWindowEvent.scroll, this.onScrollWindow);
  }

  /**
   * Return a tooltip template
   */
  private createTooltipTemplate(tooltipButton: HTMLElement): HTMLElement {
    const document = this.windowService.getNative().document;

    const tooltip = document.createElement('div');
    const contentWrap = document.createElement('div');

    tooltip.classList.add(this.cssClass.tooltip);
    contentWrap.classList.add(this.cssClass.tooltipContent);

    tooltip.appendChild(contentWrap);

    tooltipButton.insertAdjacentElement('beforeend', tooltip);
    return tooltip;
  }

  /**
   * Set the visibility of the tooltip
   */
  private setVisibility(isVisible: boolean): void {
    if (this.isVisible === isVisible) {
      return;
    }

    this.isVisible = isVisible;

    if (isVisible) {
      this.element.classList.add(this.cssClass.tooltipVisible);
    } else {
      this.element.classList.remove(this.cssClass.tooltipVisible);
    }
  }

  /**
   * Clicked element
   */
  private onClickElement = (e: Event): void => {
    e.preventDefault();
  };

  /**
   * Clicked on the window
   */
  private onClickWindow = (e: Event): void => {
    if (this.element && this.element.contains(<HTMLElement>e.target)) {
      return;
    }

    this.reset();
  };

  /**
   * Resized the window
   */
  private onResizeWindow = (): void => {
    this.reset();
  };

  /**
   * Scrolled on the window
   */
  private onScrollWindow = (): void => {
    this.reset();
  };
}
